import type { ScopeRuleUpdatePayload } from '@app-types/import';

export interface FlatScopeRule {
  id: number;
  field: string;
  operator: string;
  value: string;
  scope: 'in_scope' | 'out_of_scope';
  created_at: string | null;
  updated_at: string | null;
}

export interface FlatScopeRulesResponse {
  rules: FlatScopeRule[];
  total: number;
}

export interface ListRule {
  id?: number;
  value: string;
  include_in_scope: boolean;
  is_active: boolean;
}

export interface ThresholdRule {
  id: number;
  min_threshold: number;
  is_active: boolean;
}

export interface ScopeRules {
  tier: ListRule[];
  contract_type: ListRule[];
  category: ListRule[];
  division: ListRule[];
  amount: ThresholdRule | null;
  duration: ThresholdRule | null;
}

const LIST_FIELDS = ['tier', 'contract_type', 'category', 'division'] as const;

export function flatRulesToGrouped(flat: FlatScopeRulesResponse): ScopeRules {
  const grouped: ScopeRules = {
    tier: [],
    contract_type: [],
    category: [],
    division: [],
    amount: null,
    duration: null,
  };

  for (const rule of flat.rules) {
    if (rule.field === 'amount' || rule.field === 'duration') {
      grouped[rule.field] = {
        id: rule.id,
        min_threshold: Number(rule.value) || 0,
        is_active: true,
      };
    } else if ((LIST_FIELDS as readonly string[]).includes(rule.field)) {
      const key = rule.field as keyof Pick<ScopeRules, 'tier' | 'contract_type' | 'category' | 'division'>;
      grouped[key].push({
        id: rule.id,
        value: rule.value,
        include_in_scope: rule.scope === 'in_scope',
        is_active: true,
      });
    }
  }

  return grouped;
}

export function mergeListRules(existing: ListRule[], lookupValues: string[]): ListRule[] {
  const byValue = new Map(existing.map((rule) => [rule.value, rule]));
  const values = new Set([...existing.map((rule) => rule.value), ...lookupValues.filter(Boolean)]);

  return Array.from(values)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => byValue.get(value) ?? {
      value,
      include_in_scope: true,
      is_active: true,
    });
}

export function listRulesToPayload(ruleType: string, items: ListRule[]): ScopeRuleUpdatePayload[] {
  return items.map((item) => (
    item.id !== undefined
      ? { id: item.id, includeInScope: item.include_in_scope }
      : { ruleType, ruleValue: item.value, includeInScope: item.include_in_scope }
  ));
}
